import { Card, Col, Empty, Row, Statistic } from "antd";
import { useEffect, useState } from "react";

import request from "@/api";
import { MyTaskAction } from "./MyTaskAction";

export interface MyTaskLogisticsStat {
    key: string;
    label: string;
    count: number;
    permissions?: readonly string[];
}

interface MyTaskLogisticsStatsProps {
    loadStats?: () => Promise<MyTaskLogisticsStat[]>;
}

const numberValue = (value: unknown) => Number(value || 0);

/** 复用 Vue 物流运输待办的船运需求、船运单和油品订单统计接口。 */
export async function loadMyTaskLogisticsStats(): Promise<MyTaskLogisticsStat[]> {
    const [requirement, waybill, oil] = await Promise.all([
        request.service.get("/admin/mytask/statisc/shipping-requirement-plat"),
        request.service.get("/admin/mytask/statisc/transport-order-ship"),
        request.service.get("/admin/mytask/statisc/oil-order-snapshot")
    ]);
    const requirementData = requirement.data || {};
    const waybillData = waybill.data || {};
    const oilData = oil.data || {};
    return [
        {
            key: "quote",
            label: "待审核报价",
            count: numberValue(requirementData.waitAcceptanceStateCount),
            permissions: ["ROLE_SHIP_DEMAND_DEAL"]
        },
        {
            key: "deposit",
            label: "待付定金",
            count: numberValue(waybillData.toBePaidDepositCount),
            permissions: ["ROLE_SHIP_DEMAND_DEAL", "ROLE_SHIP_DEMAND_MANAGE"]
        },
        {
            key: "departure",
            label: "待发航",
            count: numberValue(waybillData.awaitingDepartureCount),
            permissions: ["ROLE_SHIP_DEMAND_DEAL", "ROLE_SHIP_DEMAND_MANAGE"]
        },
        {
            key: "transporting",
            label: "运输中",
            count: numberValue(waybillData.duringTransportationCount),
            permissions: ["ROLE_SHIP_DEMAND_DEAL", "ROLE_SHIP_DEMAND_MANAGE"]
        },
        {
            key: "unload",
            label: "待卸货",
            count: numberValue(waybillData.toBeUnloadedCount),
            permissions: ["ROLE_SHIP_DEMAND_DEAL", "ROLE_SHIP_DEMAND_MANAGE"]
        },
        {
            key: "unfinished",
            label: "未完成",
            count: numberValue(waybillData.nonCompletedCount),
            permissions: ["ROLE_SHIP_DEMAND_DEAL", "ROLE_SHIP_DEMAND_MANAGE"]
        },
        {
            key: "oilBooking",
            label: "待核对",
            count: numberValue(oilData.bookingCount),
            permissions: ["ROLE_GAS_ORDER_DEAL"]
        },
        {
            key: "oilPlan",
            label: "待计划",
            count: numberValue(oilData.awaitingPlan),
            permissions: ["ROLE_GAS_ORDER_DEAL"]
        },
        {
            key: "oilConfirm",
            label: "待确认加油",
            count: numberValue(oilData.awaitingConfirmation),
            permissions: ["ROLE_GAS_ORDER_DEAL"]
        },
        {
            key: "oilComplete",
            label: "待完成加油",
            count: numberValue(oilData.awaitingCompletion),
            permissions: ["ROLE_GAS_ORDER_DEAL"]
        }
    ];
}

/** 物流运输待办统计卡片，沿用 Vue 的十类核心数量展示。 */
export function MyTaskLogisticsStats({ loadStats = loadMyTaskLogisticsStats }: MyTaskLogisticsStatsProps) {
    const [stats, setStats] = useState<MyTaskLogisticsStat[]>([]);

    useEffect(() => {
        let disposed = false;
        void loadStats().then(result => {
            if (!disposed) setStats(result);
        });
        return () => {
            disposed = true;
        };
    }, [loadStats]);

    return (
        <Card title="物流运输">
            {stats.length ? (
                <Row gutter={[16, 16]}>
                    {stats.map(stat => (
                        <Col key={stat.key} xs={12} sm={8} md={6}>
                            <MyTaskAction stat={stat}>
                                <Statistic title={stat.label} value={stat.count} />
                            </MyTaskAction>
                        </Col>
                    ))}
                </Row>
            ) : (
                <Empty description="暂无数据" />
            )}
        </Card>
    );
}
